import { filterTree } from '@/shared/lib/utils/tree/filter-tree';
import { treeMap } from '@/shared/lib/utils/tree/tree-map';
import { treeSearch } from '@/shared/lib/utils/tree/tree-search';

import {
  matchScopedAction,
  PATH_SEPARATOR,
  pathStartsWith,
  type ParsedSearchQuery,
} from './match-query';
import type { AppSearchAction } from '../types';

export const findByFullPath = (
  nodes: AppSearchAction[],
  path: string[],
): AppSearchAction | null =>
  treeSearch(
    nodes,
    'children',
    (node) =>
      node.path.length === path.length && pathStartsWith(node.path, path),
  ) ?? null;

export const findActionByPath = findByFullPath;

export const isAttachSelected = (
  attachPath: string[] | null,
  scope: string[],
): boolean =>
  Boolean(
    attachPath &&
      scope.length >= attachPath.length &&
      attachPath.every(
        (segment, index) =>
          scope[index]?.toLowerCase() === segment.toLowerCase(),
      ),
  );

export const findPathByTo = (
  nodes: AppSearchAction[],
  to: string,
): string[] | null =>
  treeSearch(nodes, 'children', (node) => node.to === to)?.path ?? null;

export const prependParentPath = (
  items: AppSearchAction[],
  parent: AppSearchAction,
): AppSearchAction[] =>
  treeMap(items, 'children', (item, options) => {
    const ancestors = (options?.path ?? []).slice(0, -1);
    const ancestorPaths = ancestors.flatMap((ancestor) => ancestor.path);
    const ancestorPathKeys = ancestors.flatMap(
      (ancestor) => ancestor.pathKeys ?? [],
    );
    const path = [...parent.path, ...ancestorPaths, ...item.path];
    const pathKeys = [
      ...(parent.pathKeys ?? []),
      ...ancestorPathKeys,
      ...(item.pathKeys ?? []),
    ];

    return {
      ...item,
      path,
      pathKeys: pathKeys.length ? pathKeys : item.pathKeys,
      completion: path.join(PATH_SEPARATOR),
      enterable: Boolean(item.enterable || item.children?.length),
    };
  });

export const attachItems = (
  tree: AppSearchAction[],
  attachTo: string,
  items: AppSearchAction[],
): AppSearchAction[] =>
  treeMap(tree, 'children', (node) => {
    if (node.to !== attachTo) {
      return node;
    }

    return {
      ...node,
      enterable: true,
      children: [...(node.children ?? []), ...prependParentPath(items, node)],
    };
  });

export const filterActionTree = (
  nodes: AppSearchAction[],
  parsed: ParsedSearchQuery,
): AppSearchAction[] =>
  treeMap(
    filterTree(nodes, 'children', (node) => matchScopedAction(node, parsed)),
    'children',
    (node) => ({
      ...node,
      enterable: Boolean(node.enterable || node.children?.length),
    }),
  );

export const flattenActionTree = (
  nodes: AppSearchAction[],
): Array<AppSearchAction & { depth: number }> => {
  const flat: Array<AppSearchAction & { depth: number }> = [];

  treeMap(nodes, 'children', (node, options) => {
    flat.push({ ...node, depth: options?.depth ?? 0 });
    return node;
  });

  return flat;
};